import {
  EnvironmentProviders,
  InjectionToken,
  Type,
  makeEnvironmentProviders,
} from '@angular/core';
import { Observable } from 'rxjs';
import { Icon } from '../../types/icon.type';
import { SearchResultItem } from './search-result.model';
import { SearchScope } from './search-scope.model';

/**
 * A pluggable search source. One connector per domain (users, ledger accounts,
 * global API search, …). Registering a new connector is the only change needed
 * to add a new section to the palette.
 */
export interface SearchConnector {
  /** Stable identifier, unique across all connectors. */
  readonly id: string;

  /** Section heading shown above the connector's results, e.g. `Benutzer`. */
  readonly label: string;

  /** Sort order of the section — lower renders first. */
  readonly order: number;

  /** Default icon for results that don't bring their own (project `ICON` set). */
  readonly icon?: Icon;

  /**
   * Runs the search for the given query in the given scope.
   * Must be a cold observable — the palette cancels superseded requests via `switchMap`.
   *
   * @param query - The trimmed, non-empty search term.
   * @param scope - The active scope (e.g. object filter chip).
   * @returns {Observable<SearchResultItem[]>} The matching results, already capped by the connector.
   */
  search(query: string, scope: SearchScope): Observable<SearchResultItem[]>;
}

/**
 * Multi-provider token for globally registered connectors. Per-call overrides are
 * possible through `GlobalSearchContext.connectors` (see `openGlobalSearch`).
 */
export const SEARCH_CONNECTOR = new InjectionToken<SearchConnector[]>('SEARCH_CONNECTOR');

/**
 * Registers the given connector classes as global defaults for the search palette.
 *
 * @param connectors - Connector classes to register (each `@Injectable`).
 * @returns {EnvironmentProviders} Providers to add to the application config.
 */
export function provideSearchConnectors(
  ...connectors: Type<SearchConnector>[]
): EnvironmentProviders {
  return makeEnvironmentProviders(
    connectors.map((connector) => ({
      provide: SEARCH_CONNECTOR,
      useClass: connector,
      multi: true,
    })),
  );
}
